import { useTranslation } from 'react-i18next';

import type { FormInstance } from '@arco-design/web-react';
import { Form, Input, Select } from '@arco-design/web-react';
import { get } from 'lodash';

import { formatVendorDeviceCellLabel } from '../../../components/vendorAndDeviceRadioList';
import { buildOptionMetaPatch, resolveOptionMetaLookupKey } from '../../../platform/option-meta';
import type { AllowedGpuFilter, ComputeInventoryResourceType, ComputeWorkloadType } from './types';
import { useComputeVendorDeviceInventory } from './useComputeVendorDeviceInventory';

const FormItem = Form.Item;
const FLEX_STYLE = { display: 'flex', gap: 10 };

export interface ComputeVendorDeviceFieldsProps {
  form: FormInstance;
  fieldPrefix: string;
  resourceType: ComputeInventoryResourceType;
  workloadType?: ComputeWorkloadType;
  /** 架构（由父组件或 linkage 注入，禁止组件内跨块 Form.useWatch） */
  architecture?: string;
  hide?: boolean;
  /** GPU：展示数量下拉 */
  showQuantity?: boolean;
  /** GPU MaaS：展示资源类型联动 */
  showResourceType?: boolean;
  allowedGpus?: AllowedGpuFilter[];
  /** 容器 GPU：驱动与软件栈 */
  isContainer?: boolean;
  requireGpuSoftware?: boolean;
}

function clearDownstreamFields(form: FormInstance, fieldPrefix: string, fields: string[]) {
  fields.forEach((field) => form.setFieldValue(`${fieldPrefix}.${field}`, undefined));
}

function readSelectLabel(option: unknown): string | undefined {
  if (!option || typeof option !== 'object') return undefined;
  const label = (option as { label?: unknown }).label;
  return typeof label === 'string' && label.trim() ? label.trim() : undefined;
}

function clearVendorDeviceOptionMeta(form: FormInstance, fieldPrefix: string) {
  const formValues = form.getFieldsValue() as Record<string, unknown>;
  const key = resolveOptionMetaLookupKey(get(formValues, fieldPrefix));
  if (key) {
    form.setFieldsValue(buildOptionMetaPatch(key, undefined, formValues));
  }
}

function buildVendorDeviceMetaKey(vendor?: string, device?: string, vendorID?: string, deviceID?: string): string | undefined {
  if (vendorID && deviceID) return `${vendorID}:${deviceID}`;
  if (vendor && device) return `${vendor}:${device}`;
  return vendor ?? device;
}

function syncInventoryVendorDeviceMeta(
  form: FormInstance,
  vendor?: string,
  device?: string,
  vendorLabel?: string,
  deviceLabel?: string,
  vendorID?: string,
  deviceID?: string,
) {
  const formValues = form.getFieldsValue() as Record<string, unknown>;
  const key = buildVendorDeviceMetaKey(vendor, device, vendorID, deviceID);
  if (!key) {
    return;
  }
  const resolvedVendorLabel = vendorLabel ?? (vendor ? formatVendorDeviceCellLabel(vendor) : undefined);
  const resolvedDeviceLabel = deviceLabel ?? (device ? formatVendorDeviceCellLabel(device) : undefined);
  const label = [resolvedVendorLabel, resolvedDeviceLabel].filter(Boolean).join(' / ') || undefined;
  form.setFieldsValue(
    buildOptionMetaPatch(
      key,
      {
        value: key,
        name: label,
        label,
        displayName: resolvedDeviceLabel,
        vendor,
        device,
        vendorID,
        deviceID,
      },
      formValues,
    ),
  );
}

/** 不计价场景：厂商 + 设备下拉（可选），GPU 可含数量与容器驱动 */
export function ComputeVendorDeviceFields({
  form,
  fieldPrefix,
  resourceType,
  workloadType = 'VirtualMachine',
  architecture,
  hide,
  showQuantity = false,
  showResourceType = false,
  allowedGpus,
  isContainer = false,
  requireGpuSoftware = true,
}: ComputeVendorDeviceFieldsProps) {
  const { t } = useTranslation();
  /** 容器 GPU 弹窗：驱动与软件栈均非必填（全局） */
  const enforceGpuSoftwareRequired = requireGpuSoftware && !isContainer;

  const inventory = useComputeVendorDeviceInventory({
    resourceType,
    workloadType,
    architecture,
    allowedGpus,
    hide,
  });

  if (hide) return null;

  const resetAfterVendorChange = (vendor?: string, vendorLabel?: string) => {
    const fields = ['device', 'deviceType', 'number', 'memory', 'vendorID', 'deviceID', 'code', 'driverVersion', 'cudaVersion'];
    if (isContainer) {
      fields.push('card');
    } else if (showQuantity) {
      fields.push('name');
    }
    clearDownstreamFields(form, fieldPrefix, fields);
    syncInventoryVendorDeviceMeta(form, vendor, undefined, vendorLabel);
  };

  const resetAfterResourceTypeChange = () => {
    const fields = ['vendor', 'device', 'deviceType', 'number', 'memory', 'vendorID', 'deviceID', 'code', 'driverVersion', 'cudaVersion'];
    if (isContainer) {
      fields.push('card');
    } else if (showQuantity) {
      fields.push('name');
    }
    clearDownstreamFields(form, fieldPrefix, fields);
    clearVendorDeviceOptionMeta(form, fieldPrefix);
  };

  const applyDeviceSelection = (
    scopedResourceType: string | undefined,
    vendor: string,
    deviceValue: string | undefined,
    deviceLabel?: string,
    vendorLabel?: string,
  ) => {
    form.setFieldValue(`${fieldPrefix}.number`, undefined);
    if (isContainer) {
      form.setFieldValue(`${fieldPrefix}.card.request`, undefined);
      form.setFieldValue(`${fieldPrefix}.driverVersion`, undefined);
      form.setFieldValue(`${fieldPrefix}.cudaVersion`, undefined);
    }
    if (deviceValue) {
      const curr = inventory.getHardwareItem(scopedResourceType, vendor, deviceValue);
      form.setFieldValue(`${fieldPrefix}.deviceType`, curr?.deviceType);
      if (isContainer) {
        form.setFieldValue(`${fieldPrefix}.card.name`, get(curr, 'quantity.resourceName'));
        form.setFieldValue(`${fieldPrefix}.memory`, curr?.gpu?.videoRAM?.capacity ?? 0);
      } else if (showQuantity) {
        form.setFieldValue(`${fieldPrefix}.name`, get(curr, 'quantity.resourceName'));
      }
      form.setFieldValue(`${fieldPrefix}.vendorID`, curr?.vendorID);
      form.setFieldValue(`${fieldPrefix}.deviceID`, curr?.deviceID);
      form.setFieldValue(`${fieldPrefix}.code`, curr?.type);
      if (showResourceType && curr?.resourceType) {
        form.setFieldValue(`${fieldPrefix}.resourceType`, curr.resourceType);
      }
      syncInventoryVendorDeviceMeta(form, vendor, deviceValue, vendorLabel, deviceLabel, curr?.vendorID, curr?.deviceID);
    } else {
      form.setFieldValue(`${fieldPrefix}.deviceType`, undefined);
      if (isContainer) {
        form.setFieldValue(`${fieldPrefix}.card.name`, undefined);
        form.setFieldValue(`${fieldPrefix}.memory`, undefined);
        form.setFieldValue(`${fieldPrefix}.driverVersion`, undefined);
        form.setFieldValue(`${fieldPrefix}.cudaVersion`, undefined);
      } else if (showQuantity) {
        form.setFieldValue(`${fieldPrefix}.name`, undefined);
      }
      form.setFieldValue(`${fieldPrefix}.vendorID`, undefined);
      form.setFieldValue(`${fieldPrefix}.deviceID`, undefined);
      form.setFieldValue(`${fieldPrefix}.code`, undefined);
      syncInventoryVendorDeviceMeta(form, vendor, undefined, vendorLabel);
    }
  };

  const syncContainerCardRequest = (value: number | undefined) => {
    if (!isContainer) return;
    form.setFieldValue(`${fieldPrefix}.card.request`, value);
  };

  const handleDriverChange = (scopedResourceType: string | undefined, vendor: string, device: string, driverVersion?: string) => {
    if (!isContainer) return;
    const cudaOptions = inventory.getCudaOptions(scopedResourceType, vendor, device, driverVersion);
    const cudaVersion = cudaOptions.length === 1 ? cudaOptions[0].value : undefined;
    form.setFieldValue(`${fieldPrefix}.driverVersion`, driverVersion);
    form.setFieldValue(`${fieldPrefix}.cudaVersion`, cudaVersion);
  };

  const vendorLabel = t('product:resource.vendor', { defaultValue: '厂商' });
  const deviceLabel = t('product:resource.device', { defaultValue: '设备' });
  const gpuSelectionRequired = showQuantity || isContainer;
  const requiredRule = gpuSelectionRequired ? [{ required: true, message: t('common:required.field') }] : undefined;

  return (
    <>
      <div style={FLEX_STYLE}>
        {showResourceType ? (
          <FormItem field={`${fieldPrefix}.resourceType`} label={t('common:resource.type')}>
            <Select
              placeholder={t('common:select.placeholder')}
              options={inventory.resourceTypeOptions}
              onChange={resetAfterResourceTypeChange}
              allowClear
            />
          </FormItem>
        ) : null}

        {showResourceType ? (
          <FormItem shouldUpdate noStyle>
            {(values) => {
              const scopedResourceType = get(values, `${fieldPrefix}.resourceType`);
              const options = scopedResourceType ? inventory.getVendorOptions(scopedResourceType) : [];
              return (
                <FormItem field={`${fieldPrefix}.vendor`} label={vendorLabel} rules={requiredRule}>
                  <Select
                    placeholder={t('common:select.placeholder')}
                    onChange={(value, option) => resetAfterVendorChange(value as string | undefined, readSelectLabel(option))}
                    options={options}
                    allowClear
                  />
                </FormItem>
              );
            }}
          </FormItem>
        ) : (
          <FormItem field={`${fieldPrefix}.vendor`} label={vendorLabel} rules={requiredRule}>
            <Select
              placeholder={t('common:select.placeholder')}
              onChange={(value, option) => resetAfterVendorChange(value as string | undefined, readSelectLabel(option))}
              options={inventory.getVendorOptions()}
              allowClear
            />
          </FormItem>
        )}

        <FormItem shouldUpdate noStyle>
          {(values) => {
            const scopedResourceType = showResourceType ? get(values, `${fieldPrefix}.resourceType`) : undefined;
            const vendor = get(values, `${fieldPrefix}.vendor`);
            const vendorOptionLabel = inventory.getVendorOptions(scopedResourceType).find((item) => item.value === vendor)?.label;
            const options = vendor ? inventory.getDeviceOptions(scopedResourceType, vendor) : [];
            return (
              <FormItem field={`${fieldPrefix}.device`} label={deviceLabel} rules={requiredRule}>
                <Select
                  placeholder={t('common:select.placeholder')}
                  options={options}
                  onChange={(value, option) =>
                    applyDeviceSelection(
                      scopedResourceType,
                      vendor,
                      value as string | undefined,
                      readSelectLabel(option),
                      typeof vendorOptionLabel === 'string' ? vendorOptionLabel : undefined,
                    )
                  }
                  allowClear
                  disabled={!vendor}
                />
              </FormItem>
            );
          }}
        </FormItem>

        {showQuantity ? (
          <FormItem shouldUpdate noStyle>
            {(values) => {
              const scopedResourceType = showResourceType ? get(values, `${fieldPrefix}.resourceType`) : undefined;
              const vendor = get(values, `${fieldPrefix}.vendor`);
              const device = get(values, `${fieldPrefix}.device`);
              const options = vendor && device ? inventory.getNumberOptions(scopedResourceType, vendor, device) : [];
              return (
                <FormItem
                  field={`${fieldPrefix}.number`}
                  label={t('common:gpu.count')}
                  rules={[{ required: true, message: t('common:required.field') }]}
                >
                  <Select
                    placeholder={t('common:select.placeholder')}
                    options={options}
                    onChange={(value) => syncContainerCardRequest(value as number | undefined)}
                    disabled={!vendor || !device}
                  />
                </FormItem>
              );
            }}
          </FormItem>
        ) : null}
      </div>

      {isContainer ? (
        <div style={FLEX_STYLE}>
          <FormItem shouldUpdate noStyle>
            {(values) => {
              const scopedResourceType = showResourceType ? get(values, `${fieldPrefix}.resourceType`) : undefined;
              const vendor = get(values, `${fieldPrefix}.vendor`);
              const device = get(values, `${fieldPrefix}.device`);
              const options = vendor && device ? inventory.getDriverOptions(scopedResourceType, vendor, device) : [];
              const rules = enforceGpuSoftwareRequired && vendor && device ? [{ required: true, message: t('common:required.field') }] : [];
              return (
                <FormItem field={`${fieldPrefix}.driverVersion`} label={t('data-center:driver.short')} rules={rules}>
                  <Select
                    placeholder={t('common:select.placeholder')}
                    options={options}
                    disabled={!vendor || !device}
                    onChange={(value) => handleDriverChange(scopedResourceType, vendor, device, value)}
                    allowClear
                  />
                </FormItem>
              );
            }}
          </FormItem>
          <FormItem shouldUpdate noStyle>
            {(values) => {
              const scopedResourceType = showResourceType ? get(values, `${fieldPrefix}.resourceType`) : undefined;
              const vendor = get(values, `${fieldPrefix}.vendor`);
              const device = get(values, `${fieldPrefix}.device`);
              const driverVersion = get(values, `${fieldPrefix}.driverVersion`);
              const options = vendor && device ? inventory.getCudaOptions(scopedResourceType, vendor, device, driverVersion) : [];
              const rules = enforceGpuSoftwareRequired && vendor && device ? [{ required: true, message: t('common:required.field') }] : [];
              return (
                <FormItem field={`${fieldPrefix}.cudaVersion`} label={t('data-center:software.stack.component')} rules={rules}>
                  <Select
                    placeholder={t('common:select.placeholder')}
                    options={options}
                    disabled={!vendor || !device}
                    onChange={(value) => form.setFieldValue(`${fieldPrefix}.cudaVersion`, value)}
                    allowClear
                  />
                </FormItem>
              );
            }}
          </FormItem>
        </div>
      ) : null}

      <div style={FLEX_STYLE}>
        <FormItem field={`${fieldPrefix}.deviceType`} hidden>
          <Input />
        </FormItem>
        {showQuantity && !isContainer ? (
          <FormItem field={`${fieldPrefix}.name`} hidden>
            <Input />
          </FormItem>
        ) : null}
        {isContainer ? (
          <FormItem field={`${fieldPrefix}.card.name`} hidden>
            <Input />
          </FormItem>
        ) : null}
        <FormItem field={`${fieldPrefix}.vendorID`} hidden>
          <Input />
        </FormItem>
        <FormItem field={`${fieldPrefix}.deviceID`} hidden>
          <Input />
        </FormItem>
        {showQuantity ? (
          <FormItem field={`${fieldPrefix}.code`} hidden>
            <Input />
          </FormItem>
        ) : null}
      </div>
    </>
  );
}
